import React, { useState, useEffect, useContext } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import * as XLSX from "xlsx";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { UsersFilter } from "../../components/UsersFilter";
import { WhatsappsFilter } from "../../components/WhatsappsFilter";
import { StatusFilter } from "../../components/StatusFilter";
import useDashboard from "../../hooks/useDashboard";
import QueueSelectCustom from "../../components/QueueSelectCustom";
import moment from "moment";
import ShowTicketLogModal from "../../components/ShowTicketLogModal";
import { blue, green } from "@material-ui/core/colors";
import {
  Facebook,
  Forward,
  History,
  Instagram,
  SaveAlt,
  WhatsApp,
} from "@material-ui/icons";

const useStyles = makeStyles((theme) => ({
  container: {
    padding: "24px",
    background: "#f5f7fa",
    minHeight: "100vh",
  },
  header: {
    marginBottom: "24px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "16px",
  },
  title: {
    fontSize: "24px",
    fontWeight: 700,
    color: "#1a1a1a",
    margin: 0,
  },
  filterContainer: {
    background: "white",
    borderRadius: "8px",
    padding: "20px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    border: "1px solid #e8ecf1",
    marginBottom: "24px",
  },
  filterTitle: {
    fontSize: "14px",
    fontWeight: 700,
    color: "#1a1a1a",
    marginBottom: "16px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  filterRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "16px",
    marginBottom: "16px",
  },
  filterInput: {
    padding: "10px 12px",
    border: "1px solid #e8ecf1",
    borderRadius: "6px",
    fontSize: "13px",
    fontFamily: "inherit",
    background: "#fafbfc",
    transition: "all 0.2s ease",
    "&:focus": {
      outline: "none",
      borderColor: "#3b82f6",
      background: "white",
      boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.1)",
    },
  },
  filterLabel: {
    fontSize: "12px",
    fontWeight: 600,
    color: "#666",
    marginBottom: "6px",
    display: "block",
  },
  filterGroup: {
    display: "flex",
    flexDirection: "column",
  },
  filterActions: {
    display: "flex",
    gap: "12px",
    justifyContent: "flex-end",
    alignItems: "center",
    marginTop: "16px",
    paddingTop: "16px",
    borderTop: "1px solid #e8ecf1",
    flexWrap: "wrap",
  },
  filterButton: {
    padding: "10px 20px",
    background: "#3b82f6",
    color: "white",
    border: "none",
    borderRadius: "6px",
    fontSize: "13px",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.3s ease",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    "&:hover": {
      background: "#2563eb",
      transform: "translateY(-1px)",
      boxShadow: "0 2px 8px rgba(59, 130, 246, 0.3)",
    },
    "&:disabled": {
      background: "#cbd5e1",
      cursor: "not-allowed",
      transform: "none",
    },
  },
  filterButtonSecondary: {
    padding: "10px 20px",
    background: "#e8ecf1",
    color: "#1a1a1a",
    border: "none",
    borderRadius: "6px",
    fontSize: "13px",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.3s ease",
    "&:hover": {
      background: "#d1d5db",
    },
    "&:disabled": {
      background: "#f0f0f0",
      cursor: "not-allowed",
      color: "#999",
    },
  },
  filterButtonExport: {
    padding: "10px 20px",
    background: "#10b981",
    color: "white",
    border: "none",
    borderRadius: "6px",
    fontSize: "13px",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.3s ease",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    "&:hover": {
      background: "#059669",
      transform: "translateY(-1px)",
      boxShadow: "0 2px 8px rgba(16, 185, 129, 0.3)",
    },
    "&:disabled": {
      background: "#cbd5e1",
      cursor: "not-allowed",
      transform: "none",
    },
  },
  tabsContainer: {
    display: "flex",
    gap: "8px",
    borderBottom: "2px solid #e8ecf1",
    marginBottom: "24px",
    overflowX: "auto",
  },
  tab: {
    padding: "12px 20px",
    background: "transparent",
    border: "none",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: 600,
    color: "#666",
    borderBottom: "3px solid transparent",
    transition: "all 0.3s ease",
    whiteSpace: "nowrap",
    "&:hover": {
      color: "#1a1a1a",
    },
  },
  tabActive: {
    color: "#3b82f6",
    borderBottomColor: "#3b82f6",
  },
  statsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
    gap: "12px",
    marginBottom: "24px",
  },
  statCard: {
    background: "white",
    borderRadius: "8px",
    padding: "16px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    border: "1px solid #e8ecf1",
    textAlign: "center",
  },
  statLabel: {
    fontSize: "12px",
    color: "#666",
    fontWeight: 500,
    marginBottom: "8px",
  },
  statValue: {
    fontSize: "24px",
    fontWeight: 700,
    color: "#3b82f6",
  },
  chartsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(350px, 1fr))",
    gap: "16px",
    marginBottom: "24px",
  },
  chartCard: {
    background: "white",
    borderRadius: "8px",
    padding: "20px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    border: "1px solid #e8ecf1",
  },
  chartTitle: {
    fontSize: "14px",
    fontWeight: 600,
    color: "#1a1a1a",
    marginBottom: "16px",
  },
  barChart: {
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-around",
    height: "250px",
    gap: "12px",
    padding: "20px 0",
  },
  barItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-end",
    flex: 1,
    height: "100%",
  },
  bar: {
    width: "100%",
    background: "#3b82f6",
    borderRadius: "4px 4px 0 0",
    marginBottom: "8px",
    transition: "all 0.3s ease",
    minHeight: "30px",
    "&:hover": {
      opacity: 0.8,
    },
  },
  barLabel: {
    fontSize: "11px",
    color: "#666",
    textAlign: "center",
    wordBreak: "break-word",
  },
  barValue: {
    fontSize: "14px",
    fontWeight: 700,
    color: "#1a1a1a",
    marginBottom: "4px",
  },
  tableCard: {
    background: "white",
    borderRadius: "8px",
    padding: "20px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    border: "1px solid #e8ecf1",
    overflow: "hidden",
  },
  tableContainer: {
    overflowX: "auto",
    maxHeight: "calc(100vh - 300px)",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  tableHeader: {
    background: "#f9fafb",
    borderBottom: "2px solid #e8ecf1",
    position: "sticky",
    top: 0,
  },
  tableHeaderCell: {
    padding: "12px",
    textAlign: "left",
    fontSize: "12px",
    fontWeight: 600,
    color: "#666",
    whiteSpace: "nowrap",
  },
  tableRow: {
    borderBottom: "1px solid #e8ecf1",
    "&:hover": {
      background: "#f9fafb",
    },
  },
  tableCell: {
    padding: "12px",
    fontSize: "13px",
    color: "#1a1a1a",
  },
  statusBadge: {
    display: "inline-block",
    padding: "4px 12px",
    borderRadius: "20px",
    fontSize: "12px",
    fontWeight: 600,
  },
  statusOpen: {
    background: "#dbeafe",
    color: "#1e40af",
  },
  statusClosed: {
    background: "#fce7f3",
    color: "#831843",
  },
  statusPending: {
    background: "#fef3c7",
    color: "#92400e",
  },
  channelIcon: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
  },
  emptyState: {
    textAlign: "center",
    padding: "40px 20px",
    color: "#999",
  },
  paginationContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: "20px",
    padding: "16px",
    background: "white",
    borderRadius: "8px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    border: "1px solid #e8ecf1",
    flexWrap: "wrap",
    gap: "12px",
  },
  paginationButton: {
    padding: "6px 12px",
    border: "1px solid #e8ecf1",
    background: "white",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "12px",
    fontWeight: 600,
    color: "#1a1a1a",
    transition: "all 0.3s ease",
    "&:hover": {
      background: "#f9fafb",
      borderColor: "#3b82f6",
    },
  },
  paginationButtonActive: {
    background: "#3b82f6",
    color: "white",
    borderColor: "#3b82f6",
  },
  actionButtons: {
    display: "flex",
    gap: "6px",
  },
  actionButton: {
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: "4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "4px",
    transition: "all 0.2s ease",
    "&:hover": {
      background: "#f0f0f0",
    },
  },
}));

const Reports = () => {
  const classes = useStyles();
  const history = useHistory();
  const { getReport } = useDashboard();
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);

  const [pageSize, setPageSize] = useState(10);
  const [searchParam, setSearchParam] = useState("");
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [selectedWhatsapp, setSelectedWhatsapp] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState([]);
  const [queueIds, setQueueIds] = useState([]);
  const [userIds, setUserIds] = useState([]);
  const [dateFrom, setDateFrom] = useState(moment("1", "D").format("YYYY-MM-DD"));
  const [dateTo, setDateTo] = useState(moment().format("YYYY-MM-DD"));
  const [onlyRated, setOnlyRated] = useState(false);
  const [totalTickets, setTotalTickets] = useState(0);
  const [tickets, setTickets] = useState([]);
  const [openTicketMessageDialog, setOpenTicketMessageDialog] = useState(false);
  const [ticketOpen, setTicketOpen] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [totalStats, setTotalStats] = useState({ open: 0, closed: 0, pending: 0, rated: 0, unrated: 0 });
  const [profanityTickets, setProfanityTickets] = useState([]);
  const [loadingProfanity, setLoadingProfanity] = useState(false);

  useEffect(() => {
    handleFilter(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Buscar totais gerais para os gráficos quando as datas mudarem
    fetchTotalStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo]);

  const fetchTotalStats = async () => {
    try {
      // Buscar todos os tickets sem filtro de status para obter o total geral
      const data = await getReport({
        dateFrom,
        dateTo,
        page: 1,
        pageSize: 9999999,
      });

      const allTickets = data.tickets || [];
      setTotalStats({
        open: allTickets.filter((t) => t.status === "open").length,
        closed: allTickets.filter((t) => t.status === "closed").length,
        pending: allTickets.filter((t) => t.status === "pending").length,
        rated: allTickets.filter((t) => t.NPS).length,
        unrated: allTickets.filter((t) => !t.NPS).length,
      });
    } catch (error) {
      console.error("Erro ao buscar totais:", error);
    }
  };

  const handleSelectedUsers = (selecteds) => {
    const users = selecteds.map((t) => t.id);
    setUserIds(users);
  };

  const handleSelectedWhatsapps = (selecteds) => {
    const whatsapp = selecteds.map((t) => t.id);
    setSelectedWhatsapp(whatsapp);
  };

  const handleSelectedStatus = (selecteds) => {
    const statusFilter = selecteds.map((t) => t.status);
    setSelectedStatus(statusFilter);
  };

  const clearFilters = () => {
    setSearchParam("");
    setSelectedContactId(null);
    setSelectedWhatsapp([]);
    setSelectedStatus([]);
    setQueueIds([]);
    setUserIds([]);
    setDateFrom(moment("1", "D").format("YYYY-MM-DD"));
    setDateTo(moment().format("YYYY-MM-DD"));
    setOnlyRated(false);
    setPageNumber(1);
    setActiveTab("overview");
  };

  const handleFilter = async (pageNum) => {
    setLoading(true);
    try {
      const data = await getReport({
        searchParam,
        contactId: selectedContactId,
        whatsappId: JSON.stringify(selectedWhatsapp),
        users: JSON.stringify(userIds),
        queueIds: JSON.stringify(queueIds),
        status: JSON.stringify(selectedStatus),
        dateFrom,
        dateTo,
        page: pageNum,
        pageSize: pageSize,
        onlyRated: onlyRated ? "true" : "false",
      });

      setTotalTickets(data.totalTickets?.total || 0);
      setTickets(data.tickets || []);
      setPageNumber(pageNum);
    } catch (error) {
      toastError(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProfanityTickets = async () => {
    setLoadingProfanity(true);
    try {
      const { data } = await api.get("/tickets/profanity");
      setProfanityTickets(data || []);
    } catch (error) {
      toastError(error);
    } finally {
      setLoadingProfanity(false);
    }
  };



  const IconChannel = (channel) => {
    switch (channel) {
      case "facebook":
        return <Facebook style={{ color: "#3b5998", fontSize: "16px" }} />;
      case "instagram":
        return <Instagram style={{ color: "#e1306c", fontSize: "16px" }} />;
      case "whatsapp":
        return <WhatsApp style={{ color: "#25d366", fontSize: "16px" }} />;
      default:
        return null;
    }
  };

  const exportToExcel = async () => {
    setLoading(true);
    try {
      const data = await getReport({
        searchParam,
        contactId: selectedContactId,
        whatsappId: JSON.stringify(selectedWhatsapp),
        users: JSON.stringify(userIds),
        queueIds: JSON.stringify(queueIds),
        status: JSON.stringify(selectedStatus),
        dateFrom,
        dateTo,
        page: 1,
        pageSize: 9999999,
        onlyRated: onlyRated ? "true" : "false",
      });

      const ticketsData = (data.tickets || []).map((ticket) => ({
        ID: ticket.id,
        Conexão: ticket.whatsappName,
        Contato: ticket.contactName,
        Usuário: ticket.userName,
        Fila: ticket.queueName,
        Status: ticket.status,
        "Última Mensagem": ticket.lastMessage,
        "Data Abertura": ticket.createdAt,
        "Data Fechamento": ticket.closedAt,
        "Tempo de Atendimento": ticket.supportTime,
        NPS: ticket.NPS || "",
        "Valor da Venda": ticket.valorVenda || "-",
        "Motivo da Não Venda": ticket.motivoNaoVenda || "-",
        "Finalizado com Venda": ticket.finalizadoComVenda ? "Sim" : "Não",
      }));

      const ws = XLSX.utils.json_to_sheet(ticketsData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Relatório");
      XLSX.writeFile(wb, `relatorio-atendimentos-${moment().format("YYYY-MM-DD")}.xlsx`);
    } catch (error) {
      toastError(error);
    } finally {
      setLoading(false);
    }
  };

  // Calcular estatísticas baseado nos tickets retornados (para a tabela)
  const stats = {
    total: tickets.length,
    open: tickets.filter((t) => t.status === "open").length,
    closed: tickets.filter((t) => t.status === "closed").length,
    pending: tickets.filter((t) => t.status === "pending").length,
    rated: tickets.filter((t) => t.NPS).length,
    unrated: tickets.filter((t) => !t.NPS).length,
  };

  // Dados para gráficos - SEMPRE mostrar o total geral, independente do filtro
  const statusData = [
    { label: "Abertos", value: totalStats.open, color: "#3b82f6" },
    { label: "Fechados", value: totalStats.closed, color: "#10b981" },
    { label: "Pendentes", value: totalStats.pending, color: "#f59e0b" },
  ];

  const ratingData = [
    { label: "Avaliados", value: totalStats.rated, color: "#8b5cf6" },
    { label: "Não Avaliados", value: totalStats.unrated, color: "#ef4444" },
  ];

  const totalPages = Math.ceil(totalTickets / pageSize);

  return (
    <div className={classes.container}>
      {openTicketMessageDialog && (
        <ShowTicketLogModal
          isOpen={openTicketMessageDialog}
          handleClose={() => setOpenTicketMessageDialog(false)}
          ticketId={ticketOpen?.id}
        />
      )}

      {/* Header */}
      <div className={classes.header}>
        <h1 className={classes.title}>📊 Relatórios de Atendimentos</h1>
      </div>

      {/* Filtros */}
      <div className={classes.filterContainer}>
        <div className={classes.filterTitle}>🔍 Filtros de Busca</div>

        <div className={classes.filterRow}>
          <div className={classes.filterGroup}>
            <label className={classes.filterLabel}>Buscar Contato</label>
            <input
              type="text"
              placeholder="Digite o nome do contato..."
              value={searchParam}
              onChange={(e) => setSearchParam(e.target.value)}
              className={classes.filterInput}
            />
          </div>
          <div className={classes.filterGroup}>
            <label className={classes.filterLabel}>Conexões</label>
            <WhatsappsFilter onFiltered={handleSelectedWhatsapps} />
          </div>
          <div className={classes.filterGroup}>
            <label className={classes.filterLabel}>Status</label>
            <StatusFilter onFiltered={handleSelectedStatus} />
          </div>
          <div className={classes.filterGroup}>
            <label className={classes.filterLabel}>Usuários</label>
            <UsersFilter onFiltered={handleSelectedUsers} />
          </div>
          <div className={classes.filterGroup}>
            <label className={classes.filterLabel}>Filas</label>
            <QueueSelectCustom
              selectedQueueIds={queueIds}
              onChange={(values) => setQueueIds(values)}
            />
          </div>
        </div>

        <div className={classes.filterRow}>
          <div className={classes.filterGroup}>
            <label className={classes.filterLabel}>Data Inicial</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className={classes.filterInput}
            />
          </div>
          <div className={classes.filterGroup}>
            <label className={classes.filterLabel}>Data Final</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className={classes.filterInput}
            />
          </div>
          <div className={classes.filterGroup}>
            <label className={classes.filterLabel}>&nbsp;</label>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                fontSize: "13px",
                cursor: "pointer",
                padding: "10px 12px",
                background: "#fafbfc",
                borderRadius: "6px",
                border: "1px solid #e8ecf1",
              }}
            >
              <input
                type="checkbox"
                checked={onlyRated}
                onChange={() => setOnlyRated(!onlyRated)}
                style={{ cursor: "pointer" }}
              />
              Apenas Avaliados
            </label>
          </div>
        </div>

        <div className={classes.filterActions}>
          <button
            className={classes.filterButtonSecondary}
            onClick={clearFilters}
            disabled={loading}
          >
            ↺ Limpar Filtros
          </button>
          <button
            className={classes.filterButton}
            onClick={() => handleFilter(1)}
            disabled={loading}
          >
            {loading ? "⏳ Filtrando..." : "🔎 Filtrar"}
          </button>
          <button
            className={classes.filterButtonExport}
            onClick={exportToExcel}
            disabled={loading}
            title="Exportar para Excel"
          >
            <SaveAlt style={{ fontSize: "16px" }} /> Exportar
          </button>
        </div>
      </div>

      {/* Abas */}
      <div className={classes.tabsContainer}>
        <button
          className={`${classes.tab} ${activeTab === "overview" ? classes.tabActive : ""}`}
          onClick={() => setActiveTab("overview")}
        >
          📈 Visão Geral
        </button>
        <button
          className={`${classes.tab} ${activeTab === "details" ? classes.tabActive : ""}`}
          onClick={() => setActiveTab("details")}
        >
          📋 Detalhes
        </button>
        <button
          className={`${classes.tab} ${activeTab === "profanity" ? classes.tabActive : ""}`}
          onClick={() => {
            setActiveTab("profanity");
            fetchProfanityTickets();
          }}
        >
          ⚠️ Monitor IA
        </button>
      </div>

      {/* Conteúdo das Abas */}
      {activeTab === "overview" && (
        <>
          {/* Estatísticas */}
          <div className={classes.statsGrid}>
            <div className={classes.statCard}>
              <div className={classes.statLabel}>Total de Tickets</div>
              <div className={classes.statValue}>{stats.total}</div>
            </div>
            <div className={classes.statCard}>
              <div className={classes.statLabel}>Abertos</div>
              <div className={classes.statValue} style={{ color: "#3b82f6" }}>
                {stats.open}
              </div>
            </div>
            <div className={classes.statCard}>
              <div className={classes.statLabel}>Fechados</div>
              <div className={classes.statValue} style={{ color: "#10b981" }}>
                {stats.closed}
              </div>
            </div>
            <div className={classes.statCard}>
              <div className={classes.statLabel}>Pendentes</div>
              <div className={classes.statValue} style={{ color: "#f59e0b" }}>
                {stats.pending}
              </div>
            </div>
            <div className={classes.statCard}>
              <div className={classes.statLabel}>Avaliados</div>
              <div className={classes.statValue} style={{ color: "#8b5cf6" }}>
                {stats.rated}
              </div>
            </div>
            <div className={classes.statCard}>
              <div className={classes.statLabel}>Não Avaliados</div>
              <div className={classes.statValue} style={{ color: "#ef4444" }}>
                {stats.unrated}
              </div>
            </div>
          </div>

          {/* Gráficos */}
          <div className={classes.chartsGrid}>
            {/* Gráfico de Avaliações - Donut */}
            <div className={classes.chartCard}>
              <div className={classes.chartTitle}>📊 Avaliações</div>
              {stats.total > 0 ? (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "40px", padding: "20px", flexWrap: "wrap" }}>
                  {/* Donut Chart */}
                  <div style={{ position: "relative", width: "180px", height: "180px" }}>
                    {(() => {
                      const total = totalStats.rated + totalStats.unrated;
                      const ratedPercent = total > 0 ? (totalStats.rated / total) * 100 : 0;
                      const conic = `conic-gradient(#8b5cf6 0% ${ratedPercent}%, #ef4444 ${ratedPercent}% 100%)`;
                      
                      return (
                        <>
                          <div
                            style={{
                              width: "100%",
                              height: "100%",
                              borderRadius: "50%",
                              background: conic,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              position: "relative",
                            }}
                          >
                            <div
                              style={{
                                width: "120px",
                                height: "120px",
                                borderRadius: "50%",
                                background: "white",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexDirection: "column",
                              }}
                            >
                              <div style={{ fontSize: "28px", fontWeight: "bold", color: "#1a1a1a" }}>
                                {ratedPercent.toFixed(0)}%
                              </div>
                              <div style={{ fontSize: "11px", color: "#666" }}>Avaliados</div>
                            </div>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                  
                  {/* Legenda */}
                  <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                      <div style={{ width: "24px", height: "24px", background: "#8b5cf6", borderRadius: "4px" }} />
                      <div>
                        <div style={{ fontSize: "13px", color: "#666", marginBottom: "4px" }}>Avaliados</div>
                        <div style={{ fontSize: "22px", fontWeight: "bold", color: "#1a1a1a" }}>
                          {totalStats.rated}
                        </div>
                      </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                      <div style={{ width: "24px", height: "24px", background: "#ef4444", borderRadius: "4px" }} />
                      <div>
                        <div style={{ fontSize: "13px", color: "#666", marginBottom: "4px" }}>Não Avaliados</div>
                        <div style={{ fontSize: "22px", fontWeight: "bold", color: "#1a1a1a" }}>
                          {totalStats.unrated}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ textAlign: "center", padding: "40px 20px", color: "#999" }}>
                  Sem dados para exibir
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Aba de Detalhes */}
      {activeTab === "details" && (
        <div className={classes.tableCard}>
          {tickets.length === 0 && !loading ? (
            <div className={classes.emptyState}>
              <p>Nenhum ticket encontrado</p>
            </div>
          ) : (
            <div className={classes.tableContainer}>
              <table className={classes.table}>
                <thead className={classes.tableHeader}>
                  <tr>
                    <th className={classes.tableHeaderCell}>ID</th>
                    <th className={classes.tableHeaderCell}>Conexão</th>
                    <th className={classes.tableHeaderCell}>Contato</th>
                    <th className={classes.tableHeaderCell}>Usuário</th>
                    <th className={classes.tableHeaderCell}>Fila</th>
                    <th className={classes.tableHeaderCell}>Status</th>
                    <th className={classes.tableHeaderCell}>Última Mensagem</th>
                    <th className={classes.tableHeaderCell}>Abertura</th>
                    <th className={classes.tableHeaderCell}>Fechamento</th>
                    <th className={classes.tableHeaderCell}>Tempo</th>
                    <th className={classes.tableHeaderCell}>Valor</th>
                    <th className={classes.tableHeaderCell}>NPS</th>
                    <th className={classes.tableHeaderCell}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {tickets.map((ticket) => (
                    <tr key={ticket.id} className={classes.tableRow}>
                      <td className={classes.tableCell}>{ticket.id}</td>
                      <td className={classes.tableCell}>
                        <div className={classes.channelIcon}>
                          {IconChannel(ticket.channel)}
                          <span>{ticket.whatsappName}</span>
                        </div>
                      </td>
                      <td className={classes.tableCell}>{ticket.contactName}</td>
                      <td className={classes.tableCell}>{ticket.userName}</td>
                      <td className={classes.tableCell}>{ticket.queueName}</td>
                      <td className={classes.tableCell}>
                        <span
                          className={`${classes.statusBadge} ${
                            ticket.status === "open"
                              ? classes.statusOpen
                              : ticket.status === "closed"
                              ? classes.statusClosed
                              : classes.statusPending
                          }`}
                        >
                          {ticket.status}
                        </span>
                      </td>
                      <td
                        className={classes.tableCell}
                        style={{
                          maxWidth: "150px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {ticket.lastMessage}
                      </td>
                      <td className={classes.tableCell}>{ticket.createdAt}</td>
                      <td className={classes.tableCell}>{ticket.closedAt || "-"}</td>
                      <td className={classes.tableCell}>{ticket.supportTime}</td>
                      <td className={classes.tableCell}>
                        {ticket.valorVenda ? (
                          <span style={{ 
                            color: "#10b981", 
                            fontWeight: 700,
                            fontSize: "13px"
                          }}>
                            R$ {parseFloat(ticket.valorVenda).toFixed(2)}
                          </span>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td className={classes.tableCell}>{ticket.NPS || "-"}</td>
                      <td className={classes.tableCell}>
                        <div className={classes.actionButtons}>
                          <button
                            className={classes.actionButton}
                            onClick={() => {
                              setOpenTicketMessageDialog(true);
                              setTicketOpen(ticket);
                            }}
                            title="Ver Logs"
                          >
                            <History fontSize="small" style={{ color: blue[700] }} />
                          </button>
                          <button
                            className={classes.actionButton}
                            onClick={() => history.push(`/tickets/${ticket.uuid}`)}
                            title="Acessar Ticket"
                          >
                            <Forward fontSize="small" style={{ color: green[700] }} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {loading && (
                    <tr>
                      <td
                        colSpan="13"
                        className={classes.tableCell}
                        style={{ textAlign: "center", padding: "20px" }}
                      >
                        Carregando...
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Aba Monitor IA */}
      {activeTab === "profanity" && (
        <div className={classes.tableCard}>
          <div style={{ marginBottom: "16px", display: "flex", alignItems: "center", gap: "12px" }}>
            <span style={{ fontSize: "16px", fontWeight: 600 }}>⚠️ Tickets com Palavrões Detectados pela IA</span>
            <span style={{ 
              padding: "4px 12px", 
              background: "#ef4444", 
              color: "white", 
              borderRadius: "12px", 
              fontSize: "12px", 
              fontWeight: 700 
            }}>
              {profanityTickets.length}
            </span>
          </div>
          {loadingProfanity ? (
            <div className={classes.emptyState}>
              <p>Carregando...</p>
            </div>
          ) : profanityTickets.length === 0 ? (
            <div className={classes.emptyState}>
              <p>✅ Nenhum ticket com palavrões detectados</p>
            </div>
          ) : (
            <div className={classes.tableContainer}>
              <table className={classes.table}>
                <thead className={classes.tableHeader}>
                  <tr>
                    <th className={classes.tableHeaderCell}>ID</th>
                    <th className={classes.tableHeaderCell}>Contato</th>
                    <th className={classes.tableHeaderCell}>Usuário</th>
                    <th className={classes.tableHeaderCell}>Contagem</th>
                    <th className={classes.tableHeaderCell}>Última Detecção</th>
                    <th className={classes.tableHeaderCell}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {profanityTickets.map((ticket) => (
                    <tr key={ticket.id} className={classes.tableRow}>
                      <td className={classes.tableCell}>
                        <span style={{ 
                          padding: "4px 8px", 
                          background: "#ef4444", 
                          color: "white", 
                          borderRadius: "6px", 
                          fontSize: "12px", 
                          fontWeight: 700 
                        }}>
                          #{ticket.id}
                        </span>
                      </td>
                      <td className={classes.tableCell}>{ticket.contact?.name || "-"}</td>
                      <td className={classes.tableCell}>{ticket.user?.name || "Sem usuário"}</td>
                      <td className={classes.tableCell}>
                        <span style={{ 
                          padding: "4px 12px", 
                          background: "#fef3c7", 
                          color: "#92400e", 
                          borderRadius: "12px", 
                          fontSize: "12px", 
                          fontWeight: 700 
                        }}>
                          {ticket.profanityCount || 1}x
                        </span>
                      </td>
                      <td className={classes.tableCell}>
                        {ticket.lastProfanityAt 
                          ? new Date(ticket.lastProfanityAt).toLocaleString("pt-BR")
                          : "-"}
                      </td>
                      <td className={classes.tableCell}>
                        <div className={classes.actionButtons}>
                          <button
                            className={classes.actionButton}
                            onClick={() => history.push(`/tickets/${ticket.uuid}`)}
                            title="Acessar Ticket"
                            style={{ 
                              background: "#ef4444", 
                              color: "white", 
                              padding: "6px 12px", 
                              borderRadius: "6px",
                              fontSize: "12px",
                              fontWeight: 600
                            }}
                          >
                            Ver Ticket
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Paginação */}
      {tickets.length > 0 && activeTab === "details" && (
        <div className={classes.paginationContainer}>
          <div style={{ fontSize: "13px", color: "#666" }}>
            Mostrando {tickets.length} de {totalTickets} registros
          </div>
          <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
            <button
              className={classes.paginationButton}
              onClick={() => handleFilter(Math.max(1, pageNumber - 1))}
              disabled={pageNumber === 1 || loading}
            >
              ← Anterior
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const page = i + 1;
              return (
                <button
                  key={page}
                  className={`${classes.paginationButton} ${
                    pageNumber === page ? classes.paginationButtonActive : ""
                  }`}
                  onClick={() => handleFilter(page)}
                  disabled={loading}
                >
                  {page}
                </button>
              );
            })}
            <button
              className={classes.paginationButton}
              onClick={() => handleFilter(Math.min(totalPages, pageNumber + 1))}
              disabled={pageNumber === totalPages || loading}
            >
              Próximo →
            </button>
          </div>
          <select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            className={classes.filterInput}
            style={{ width: "80px" }}
          >
            <option value={5}>5 por página</option>
            <option value={10}>10 por página</option>
            <option value={20}>20 por página</option>
            <option value={50}>50 por página</option>
          </select>
        </div>
      )}
    </div>
  );
};

export default Reports;
